import React from 'react';
import classnames from 'classnames';
import './VenueAnalysisGrid.scss';
import { VenueAnalysisData } from '../venueAnalysisAggregatedData';
import {
    computeBarWidths,
    VenueAnalysisVenueDisplayData,
    getSortedVenueTypes,
    filterByVenueType,
    filterByVenue,
} from './gridData';
import { VenueAnalysisBar } from './VenueAnalysisBar';
import { VenueAnalysisToggleLabel } from './VenueAnalysisToggleLabel';
import ShowHideToggle from 'utils/components/ShowHideToggle/ShowHideToggle';
import { VenueAnalysisConfigurationState } from '../store/configuration/VenueAnalysisConfigurationState';
import { toggleVenueAnalysisComponentProperty } from '../store/component/VenueAnalysisComponentState';
import { orderTooltip } from 'widgets/venueAnalysis/help/venueAnalysisTooltips';
import {
    isVenueSelected,
    VenueAnalysisSelection,
} from 'widgets/venueAnalysis/store/component/VenueAnalysisSelection';
import { HighlightedVenue } from 'widgets/venueAnalysis/highlighting';
import './OrderVenueBreakdown.scss';
import { useVenueAnalysisComponentSlice } from 'widgets/venueAnalysis/store/component/componentSlice';
import { OrderLabel } from 'widgets/venueAnalysis/grid/OrderLabel.tsx';

interface OrderVenueBreakdownProps {
    data: VenueAnalysisData;
    config: VenueAnalysisConfigurationState;
    onToggle: () => void;
    highlightedVenue?: HighlightedVenue;
    onHighlightedVenueChange: (venue?: HighlightedVenue) => void;
    selection?: VenueAnalysisSelection;
    onChangeSelection: (value?: VenueAnalysisSelection) => void;
    widgetKey: string;
    width: number;
}

export function OrderVenueBreakdown({
    data,
    config,
    onToggle,
    highlightedVenue,
    onHighlightedVenueChange,
    selection,
    onChangeSelection,
    width,
}: OrderVenueBreakdownProps) {
    const {
        visibleSections: { orders: isVisible },
    } = config;

    const orderVenueTypes = data?.orderVenue?.venueTypes;

    const { component, dispatchComponentChanged: updateComponent } =
        useVenueAnalysisComponentSlice();

    const toggleBreakdown = () =>
        updateComponent(
            toggleVenueAnalysisComponentProperty(
                component,
                'visibleBreakdowns',
                'orderVenue'
            )
        );

    const showVenueBreakdown = component.visibleBreakdowns.orderVenue;

    let displayData: VenueAnalysisVenueDisplayData[] = orderVenueTypes
        ? computeBarWidths(getSortedVenueTypes(orderVenueTypes))
        : [];

    if (selection) {
        displayData = filterByVenueType(displayData, selection.venueTypeName);
    }

    if (isVenueSelected(selection)) {
        displayData = filterByVenue(displayData, selection.venueName);
    }

    return (
        <div className="OrderVenueBreakdown">
            <div
                className={classnames(
                    'grid-row',
                    'orders',
                    'section-header',
                    !isVisible && 'section-hidden'
                )}
                onClick={onToggle}
            >
                <div className="labels">
                    <div className="header-title">Order</div>{' '}
                </div>
                <div className="content">
                    <div className="header-text">
                        <OrderLabel data={data} config={config} />
                    </div>
                    <ShowHideToggle status={isVisible ? 'hide' : 'show'} />
                </div>
            </div>
            {isVisible && !!data?.orderVenue?.qty && (
                <div className="grid-row venue-bar-row orders">
                    <div className="labels">
                        <VenueAnalysisToggleLabel
                            name="Order"
                            showVenues={showVenueBreakdown}
                            onClick={() => toggleBreakdown()}
                            tooltip={orderTooltip}
                        />
                    </div>
                    <div
                        className={classnames(
                            'content',
                            selection && 'single-type'
                        )}
                    >
                        {displayData.map((venueType) => (
                            <VenueAnalysisBar
                                data={venueType}
                                showVenues={showVenueBreakdown}
                                key={venueType.name}
                                highlightedVenue={highlightedVenue}
                                onHighlightedVenueChange={
                                    onHighlightedVenueChange
                                }
                                onSelectionChanged={onChangeSelection}
                                selection={selection}
                                width={width}
                                showTypeToVenueSankey={isVenueSelected(
                                    selection
                                )}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
